import React, { useEffect, useState } from "react";
import { Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import {
  loadSymptomRiskForecast,
  type SymptomRiskForecast,
  type SignalSnapshot,
} from "./symptomRiskForecast";
import { INSIGHT_DISCLAIMER, INSIGHTS } from "./insightsLibrary";

const PLUM = "#4B1F4F";
const ROSE = "#FF5C7A";
const ROSE_LIGHT = "#FFC1CD";
const CREAM = "#FAF7F5";
const LINE = "#E8D5DA";
const MUTED = "#6E4B71";
const INK = "#3A1A3E";

interface Props {
  recentLogs: { log_date: string; symptom_name: string }[];
  todayKey: string;
}

function bandColor(band: SymptomRiskForecast["band"]): { bg: string; fg: string; border: string } {
  if (band === "Elevated") return { bg: "#FFE9EE", fg: "#8A1F3A", border: "#F2B7C4" };
  if (band === "Moderate") return { bg: "#FFF3E6", fg: "#7A4A18", border: "#F0D3B0" };
  return { bg: "#EAF6EE", fg: "#1F5A36", border: "#BFE3CB" };
}

// Recovery signals drive the headline — they're the intuitive, defensible
// next-day indicators. Skin temperature is handled separately as a reflection
// on last night's night sweats, NOT as a forecast signal for today.
const RECOVERY_KEYS: SignalSnapshot["key"][] = ["sleep", "hrv", "resting_hr"];

function recoveryPhrase(s: SignalSnapshot): string {
  switch (s.key) {
    case "hrv":
      if (s.riskDirection === "higher") return "your HRV was lower than usual";
      if (s.riskDirection === "lower") return "your HRV was higher than usual";
      return "your HRV is close to your usual";
    case "resting_hr":
      if (s.riskDirection === "higher") return "your resting heart rate was higher than usual";
      if (s.riskDirection === "lower") return "your resting heart rate was lower than usual";
      return "your resting heart rate is close to your usual";
    case "sleep":
      if (s.riskDirection === "higher") return "your sleep ran shorter than usual";
      if (s.riskDirection === "lower") return "your sleep held steady (above your usual)";
      return "your sleep is close to your usual";
    case "skin_temperature":
      // Not used in headline — see skinTempNote.
      return "";
    case "respiratory_rate":
      // Not a recovery signal — not used in headline.
      return "";
  }
}

function valueSnippet(s: SignalSnapshot): string {
  if (s.key === "sleep") {
    const h = Math.floor(s.lastNight / 60);
    const m = Math.round(s.lastNight - h * 60);
    return `${h}h ${m.toString().padStart(2, "0")}m`;
  }
  if (s.key === "hrv") return `${Math.round(s.lastNight)} ms`;
  if (s.key === "resting_hr") return `${Math.round(s.lastNight)} bpm`;
  // Skin temp delta in °F (delta = °C × 1.8, no +32).
  const f = s.delta * 1.8;
  return `${f >= 0 ? "+" : ""}${f.toFixed(2)}°F`;
}

function joinPhrases(parts: string[]): string {
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(", ")}, and ${parts[parts.length - 1]}`;
}

interface OutlookCopy {
  lead: string;
  skinTempNote: string | null;
}

function buildOutlook(f: SymptomRiskForecast): OutlookCopy {
  const recovery = f.signalSnapshots.filter((s) => RECOVERY_KEYS.includes(s.key));
  const skin = f.signalSnapshots.find((s) => s.key === "skin_temperature") ?? null;

  // Skin temp note — framed as a reflection on the night that just passed,
  // tied to night sweats only. Never folded into today's outlook.
  let skinTempNote: string | null = null;
  if (skin) {
    if (skin.riskDirection === "higher") {
      skinTempNote = "Your skin temperature ran warm overnight, which can go along with night sweats and disrupted sleep.";
    } else {
      skinTempNote = "Your skin temperature stayed steady overnight — often a sign of fewer night sweats.";
    }
  }

  if (f.band === "Elevated" || f.band === "Moderate") {
    // Headline uses fired RECOVERY signals only (sleep / HRV / RHR). If only
    // skin temp fired, fall back to the strained recovery signals we have so
    // we never imply skin temp predicts today's symptoms.
    const firedRecovery = f.contributingSignals
      .filter((c) => RECOVERY_KEYS.includes(c.key))
      .map((c) => recovery.find((s) => s.key === c.key))
      .filter((s): s is SignalSnapshot => !!s)
      .slice(0, 2);
    const headlineSignals = firedRecovery.length
      ? firedRecovery
      : recovery.filter((s) => s.riskDirection === "higher").slice(0, 2);

    const prefix = f.band === "Elevated"
      ? "Higher-symptom outlook today"
      : "Slightly higher-symptom outlook today";

    if (headlineSignals.length === 0) {
      return {
        lead: `${prefix}. Your overnight recovery was strained, which often tracks with tougher symptom days.`,
        skinTempNote,
      };
    }
    const phrases = headlineSignals.map((s) => `${recoveryPhrase(s)} (${valueSnippet(s)})`);
    return {
      lead: `${prefix}. Your overnight recovery was strained — ${joinPhrases(phrases)}, which often lines up with tougher symptom days.`,
      skinTempNote,
    };
  }

  // Lower band: prefer the strongest recovery signals (lower-risk or near).
  const candidates = recovery
    .filter((s) => s.riskDirection !== "higher")
    .sort((a, b) => {
      const rank = (d: SignalSnapshot["riskDirection"]) => (d === "lower" ? 0 : d === "near" ? 1 : 2);
      return rank(a.riskDirection) - rank(b.riskDirection);
    })
    .slice(0, 2);

  if (candidates.length === 0) {
    return {
      lead: "Lower-symptom outlook today. Your overnight recovery signals are close to your usual baseline.",
      skinTempNote,
    };
  }
  const phrases = candidates.map((s) => `${recoveryPhrase(s)} (${valueSnippet(s)})`);
  const allBetter = candidates.every((s) => s.riskDirection === "lower");
  const tail = allBetter
    ? "which tends to line up with easier days."
    : "which tends to line up with steadier days.";
  return {
    lead: `Lower-symptom outlook today. Your overnight signals were close to your usual — ${joinPhrases(phrases)}, ${tail}`,
    skinTempNote,
  };
}

// Daily-rotating general tip — independent of the forecast so the educational
// content never looks like a "because-X-therefore-hot-flashes" claim.
function dailyTipText(dateKey: string): string {
  const d = new Date(`${dateKey}T00:00:00`);
  const start = new Date(d.getFullYear(), 0, 0);
  const dayOfYear = Math.floor((d.getTime() - start.getTime()) / 86400000);
  return INSIGHTS[dayOfYear % INSIGHTS.length].text;
}

export function SymptomOutlookCard({ recentLogs: _recentLogs, todayKey }: Props) {
  const [forecast, setForecast] = useState<SymptomRiskForecast | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase.auth.getUser();
        const uid = data?.user?.id;
        if (!uid) { if (!cancelled) setLoading(false); return; }
        const f = await loadSymptomRiskForecast(uid);
        if (!cancelled) { setForecast(f); setLoading(false); }
      } catch {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const isOk = forecast?.status === "ok";
  const isBuilding = forecast?.status === "building_baseline";
  const hasForecast = isOk;

  const outlook = hasForecast ? buildOutlook(forecast!) : null;
  const tipText = dailyTipText(todayKey);

  if (loading) return null;

  const colors = hasForecast
    ? bandColor(forecast!.band)
    : { bg: CREAM, fg: MUTED, border: LINE };

  return (
    <div
      style={{
        background: "#FFFFFF",
        border: `1.5px solid ${ROSE_LIGHT}`,
        borderRadius: 18,
        padding: "14px 14px 12px",
        marginBottom: 14,
        boxShadow: "0 8px 24px -18px rgba(74,25,66,0.3)",
      }}
      role="region"
      aria-label="Today's outlook"
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              width: 28, height: 28, borderRadius: "50%",
              background: CREAM, border: `1px solid ${LINE}`,
              display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto",
            }}
            aria-hidden
          >
            <Sparkles size={15} color={ROSE} strokeWidth={2} />
          </span>
          <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: PLUM }}>
            Today's Outlook
          </span>
        </div>
        {hasForecast && forecast!.band ? (
          <span
            style={{
              fontSize: 10.5, fontWeight: 600, color: colors.fg,
              letterSpacing: "0.04em", textTransform: "uppercase",
              background: colors.bg, border: `1px solid ${colors.border}`,
              borderRadius: 999, padding: "2px 8px",
            }}
          >
            {forecast!.band}
          </span>
        ) : null}
      </div>

      {/* Headline: recovery-driven outlook (or building-baseline notice) */}
      {hasForecast ? (
        <>
          <div style={{ fontSize: 13.5, color: INK, lineHeight: 1.5, marginBottom: outlook!.skinTempNote ? 8 : 12 }}>
            {outlook!.lead}
          </div>
          {outlook!.skinTempNote ? (
            <div
              style={{
                fontSize: 12.5, color: INK, lineHeight: 1.5, marginBottom: 12,
                background: CREAM, border: `1px solid ${LINE}`, borderRadius: 10,
                padding: "8px 10px",
              }}
            >
              <span style={{ fontSize: 10, fontWeight: 600, color: PLUM, textTransform: "uppercase", letterSpacing: "0.08em", marginRight: 6 }}>
                Last night
              </span>
              {outlook!.skinTempNote}
            </div>
          ) : null}
        </>
      ) : isBuilding ? (
        <div style={{ fontSize: 13.5, color: INK, lineHeight: 1.5, marginBottom: 12 }}>
          Building your personal baseline — connect your wearable and check back in a few days for a tailored daily outlook.
        </div>
      ) : null}

      {hasForecast && forecast!.signalSnapshots.length > 0 ? (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          style={{
            background: "transparent", border: "none", padding: 0,
            color: PLUM, fontSize: 12, fontWeight: 600,
            display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer",
          }}
          aria-expanded={expanded}
        >
          Why? {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </button>
      ) : null}

      {expanded && hasForecast && forecast!.signalSnapshots.length > 0 ? (
        <div
          style={{
            background: CREAM, border: `1px solid ${LINE}`, borderRadius: 12,
            padding: "10px 12px", marginTop: 8,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: PLUM, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
            Last night vs your baseline
          </div>
          <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 6 }}>
            {forecast!.signalSnapshots
              .filter((s) => s.key !== "skin_temperature")
              .map((s) => (
                <li key={s.key} style={{ fontSize: 12.5, color: INK, lineHeight: 1.45 }}>
                  <strong style={{ color: PLUM }}>{s.label}:</strong> {s.display}
                </li>
              ))}
          </ul>
        </div>
      ) : null}

      {/* Educational tip — visually separated, NOT tied to today's prediction. */}
      <div
        style={{
          marginTop: 14,
          paddingTop: 12,
          borderTop: `0.5px solid ${LINE}`,
        }}
      >
        <div style={{ fontSize: 10.5, fontWeight: 600, color: MUTED, textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: 6 }}>
          Tip
        </div>
        <div style={{ fontSize: 13, color: INK, lineHeight: 1.5 }}>{tipText}</div>
      </div>

      <div
        style={{
          fontSize: 11, color: MUTED, fontStyle: "italic", lineHeight: 1.4,
          borderTop: `0.5px solid ${LINE}`, paddingTop: 8, marginTop: 12,
        }}
      >
        {hasForecast
          ? "This is a pattern-based outlook, not a prediction. Alcohol, stress, room temperature, and illness can all affect symptoms."
          : INSIGHT_DISCLAIMER}
      </div>
    </div>
  );
}

export default SymptomOutlookCard;
